import { useEffect, useCallback, useRef } from 'react';
import { getSocket } from '../lib/socket'; 
import type { EngagementSnapshot } from '@/types/emotion';

type EngagementEmitterProps = {
  sessionId: string;
  studentId: string;
  role: 'student' | 'instructor';
  enabled?: boolean;
  onServerUpdate?: (data: { sessionId: string; snapshots: EngagementSnapshot[] }) => void;
};

// ⏱️ AGGREGATION CONFIG
// 2000ms = Good balance for demo (charts move visibly)
const AGGREGATION_WINDOW_MS = 2000;

export const useEngagementEmitter = ({
  sessionId,
  studentId,
  role,
  enabled = true,
  onServerUpdate
}: EngagementEmitterProps) => {
  
  const socket = getSocket();
  
  // 1. Local Buffer (Stores high-speed frames from AI)
  const bufferRef = useRef<Omit<EngagementSnapshot, 'sessionId' | 'studentId'>[]>([]);

  // 2. Student Name (For heatmap display)
  const studentNameRef = useRef<string>('Anonymous');

  // ✅ FIX: Use dynamic type instead of NodeJS.Timeout
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!enabled || !sessionId) return;

    if (!socket.connected) {
      socket.connect();
    }

    socket.emit('session:join', { sessionId });

    const handleUpdate = (data: { sessionId: string; snapshots: EngagementSnapshot[] }) => {
      if (onServerUpdate) {
        onServerUpdate(data);
      }
    };

    socket.on('engagement:update', handleUpdate);

    return () => {
      socket.off('engagement:update', handleUpdate);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionId, enabled, onServerUpdate]);

  // 2. The Publisher (Now just adds to buffer)
  const publish = useCallback((
    emotions: Omit<EngagementSnapshot, 'sessionId' | 'studentId' | 'studentName'>, 
    studentName?: string  // <--- This argument is required for the code to work
  ) => {
    if (!enabled || role !== 'student') return;
    
    // Now "studentName" exists, so this line won't error
    if (studentName) {
      studentNameRef.current = studentName;
    }
  
    // Push raw data to buffer, no socket cal yet!!
    bufferRef.current.push(emotions);
  }, [enabled, role]);

  // 3. The Aggregator Loop (Runs every 2 seconds)
  useEffect(() => {
    if (!enabled || role !== 'student') return;

    timerRef.current = setInterval(() => {
      const frames = bufferRef.current;
      if (frames.length === 0) return;

      // 🧮 MATH: Average all frames in the buffer
      const count = frames.length;
      
      const totals = frames.reduce((acc, curr) => ({
        engaged: acc.engaged + (curr.engaged || 0),
        confused: acc.confused + (curr.confused || 0),
        disengaged: acc.disengaged + (curr.disengaged || 0),
        neutral: acc.neutral + (curr.neutral || 0),
        happy: acc.happy + (curr.happy || 0),
        sad: acc.sad + (curr.sad || 0),
        angry: acc.angry + (curr.angry || 0),
        surprise: acc.surprise + (curr.surprise || 0),
        fear: acc.fear + (curr.fear || 0),
        disgust: acc.disgust + (curr.disgust || 0),
      }), { engaged: 0, confused: 0, disengaged: 0, neutral: 0, happy: 0, sad: 0, angry: 0, surprise: 0, fear: 0, disgust: 0 });

      const averagedSnapshot: EngagementSnapshot = {
        sessionId,
        studentId,
        studentName: studentNameRef.current,
        timestamp: new Date().toISOString(),
        windowSeconds: AGGREGATION_WINDOW_MS / 1000, 
        engaged: totals.engaged / count,
        confused: totals.confused / count,
        disengaged: totals.disengaged / count,
        neutral: totals.neutral / count,
        happy: totals.happy / count,
        sad: totals.sad / count,
        angry: totals.angry / count,
        surprise: totals.surprise / count,
        fear: totals.fear / count,
        disgust: totals.disgust / count,
      };

      // 🚀 EMIT: Send ONE clean packet to backend
      if (socket.connected) {
        socket.emit('engagement:publish', averagedSnapshot);
      }

      // Clear buffer for next window
      bufferRef.current = [];

    }, AGGREGATION_WINDOW_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionId, studentId, enabled, role]);

  return { publish };
};